import { SenderType } from '@/const/mission';

import { RichMediaValue } from './media';
import { StoryLayout } from './module';

export enum Status {
  DEFAULT = 0,
  WAITING = 1,
  SUCCESS = 2,
  FINISHED = 3,
  FAILED = 4,
}

export enum Mode {
  IMG2IMG = 'img2img',
  TXT2IMG = 'txt2img'
}

export enum AiVideoType {
  TEXT = 0,
  IMAGE = 1,
}

export enum VideoImageRole {
  FIRST = 0, // 首帧
  LAST = 1, // 尾帧
}

export enum AiType {
  IMAGE = 'picture',
  MUSIC = 'music',
  VIDEO = 'video',
}

export const AiTypeLabel = {
  [AiType.IMAGE]: 'AI智能绘图',
  [AiType.MUSIC]: 'AI智能音乐',
  [AiType.VIDEO]: 'AI智能视频',
};

export enum VoiceGender {
  ALL = 0,
  WOMAN = 1,
  MAN = 2,
  OTHER = 3,
}

export enum VoiceAge {
  OTHER = 0,
  CHILD = 1,
  TEENAGER = 2,
  OLD = 3,
}

// 后端定义的语音包来源
export enum VoiceType {
  UNKNOWN = 0,
  CAI = 1,
  ZHIYING = 2, // 智影（已废弃）
  MINIMAX = 3,
  HUNYUAN = 4,
  HUNYUAN_IPBOT = 5,
}

export enum AiImageModelType {
  AI_IMAGE_UNKNOWN = 0,
  AI_IMAGE_HUNYUAN = 1,
  AI_IMAGE_MIDJOURNEY = 2,
  AI_IMAGE_JIMENG_30 = 3,
  AI_IMAGE_JIMENG_31 = 4,

  // 暂不支持
  // AI_IMAGE_FANGZHOU_40 = 5,
  // AI_IMAGE_JIMENG_40 = 6,
}

export const DEFAULT_AI_IMAGE_MODEL = AiImageModelType.AI_IMAGE_JIMENG_30;

export enum AiImageReferenceType {
  DEFAULT = 0,
  CW = 1, // 形象参考
  SW = 2, // 风格参考
  AUTO = 3, // 智能参考
}

export const AiImageReferenceTypeLabel = {
  [AiImageReferenceType.DEFAULT]: '默认',
  [AiImageReferenceType.CW]: '形象参考',
  [AiImageReferenceType.SW]: '风格参考',
  [AiImageReferenceType.AUTO]: '智能参考',
};

export const AiImageReferenceNumber = {
  [AiImageReferenceType.CW]: 36, // 形象参考默认 0.36
  [AiImageReferenceType.SW]: 60, // 风格参考默认 0.6
};

export const AiImageModel = {
  // HUNYUAN: {
  //   value: AiImageModelType.AI_IMAGE_HUNYUAN,
  //   label: '1.0',
  // },
  // MIDJOURNEY: {
  //   value: AiImageModelType.AI_IMAGE_MIDJOURNEY,
  //   label: '2.0（推荐）',
  // },
  JIMENG_30: {
    value: AiImageModelType.AI_IMAGE_JIMENG_30,
    label: '经典版（生图稳定性强，图片有质感）',
  },
  JIMENG_31: {
    value: AiImageModelType.AI_IMAGE_JIMENG_31,
    label: '灵动版（美学大幅提升，画面更生动）',
  },
};

export const AiImageResolution = {
  '1K': {
    value: 1,
    label: '标清 1K',
  },
  '2K': {
    value: 2,
    label: '高清 2K',
  },
};

// prompt 长度限制
export const AIGC_IMAGE_PROMPT_LENGTH = 800;
export const AIGC_MUSIC_PROMPT_LENGTH = 100;
export const AIGC_VIDEO_PROMPT_LENGTH = 500;

// export const AIGC_STYLE_DEFAULT = '拟真厚涂';

export const AIGC_STYLE_CUSTOM = '自定义';

export const AIGC_IMAGE_DIMENTION = {
  [StoryLayout.LAYOUT_VERTICAL]: {
    baseWidth: 720,
    baseHeight: 1280,
  },
  [StoryLayout.LAYOUT_HORIZONTAL]: {
    baseWidth: 1280,
    baseHeight: 720,
  },
};

export interface Voice {
  voiceId: string;
  voiceName?: string;
  tag?: string[];
  gender?: VoiceGender; // 0-全部 1-女 2-男 3-其他
  ageGroup?: VoiceAge;
  url?: string; // 默认播放语音url
  voiceType?: VoiceType; // 区分语音包来源
  speed?: number; // 语速 [0.5, 2] 默认1
  pitch?: number; // 语调 [-12, 12] 默认0
  emotion?: string; // 情绪

  // 仅供后端用
  vol?: number; // 音量
  timberWeights?: TimberWeight[]; // 音色混合
  cloneCosurl?: string; // 克隆语音url
  cloneText?: string; // 克隆语音文本
}

export interface TimberWeight {
  weight: number;
  voiceId: string;
}

export interface UrlInfo {
  url: string;
  name: string;
  errMsg?: string;
  volume?: number;
}

export interface SrefInfo {
  imageUrl: string;
  sw: number;
}

export interface CrefInfo {
  imageUrl: string;
  cw: number;
}

export interface VideoImage {
  url: string;
  role: VideoImageRole;
}

export interface MjReq {
  mjStyleAlias?: string;
  mjPic?: string;
  crefInfo?: CrefInfo;
  srefInfo?: SrefInfo;
}

export interface AiStyleInfo {
  style?: string;
  mjStyleAlias?: string;
  mjPic?: string;
}

export interface AiImageReq {
  text: string; // max_len: 800
  baseWidth: number;
  baseHeight: number;
  mode: Mode;
  imageUrl?: string;
  queryId?: string; // storyId || botId
  negativeText?: string; // max_len: 800
  model?: AiImageModelType;
  style?: string;
  mjReq?: MjReq;
  referenceType?: AiImageReferenceType;
}

export interface AiMusicReq {
  prompt?: string;
  queryId?: string;
  genre?: string[];
  mood?: string[];
  theme?: string[];
}

export interface AiVoiceReq {
  voiceInfo: Voice;
  text: string;
  senderType?: SenderType;
}

export interface AiVideoReq {
  queryId?: string;
  type: AiVideoType;
  content: {
    text: string;
    duration?: number;
    ratio?: string;
    imageUrl?: VideoImage[];
  }
}

export interface AiTaskRsp {
  relateId: string;
  userLevelType: UserLevel;
}

export interface GetAIResourceReq {
  queryId: string;
  type: AiType;
  page?: number;
  size?: number;
  model?: AiImageModelType;
}

export interface GetAIResourceRsp {
  data: ResourceHistory[];
  totalCnt: number;
}

export interface DeleteAIResourceReq {
  relateIds: string[];
}

export interface DeleteAIResourceRsp {
  hasDeleted: boolean;
}

export interface ResourceHistory {
  urls: UrlInfo[];
  prompt: string;
  status: Status;
  relateId: string;
  ratio?: string;
  style?: string; // 绘图风格
  aiTxt2ImgReq?: string; // 绘图请求
}

export interface Model {
  id: string;
  name: string;
  cover_url: string;
}

export interface FetchModelsResp {
  models: Model[]
}

export interface PolishPromptReq {
  text: String;
}

export interface PolishPromptResp {
  polishedText: string;
}

export interface GetTasksByIDReq {
  task_id: string;
}

export interface GetTasksByIDResp {
  text: string;
  status: Status;
}

export interface RemoveBackgroundReq {
  image_url: string;
}

export interface RemoveBackgroundResp {
  after_process_url: string;
}

export enum OutPaintType {
  OUTPAINT_TYPE_OUTPAINTING = 1, // 正常扩图接口
  OUTPAINT_TYPE_SEEDEDIT = 2, // 指令编辑（智能参考）
}

export interface OutPaintReq {
  image_url: string;
  custom_prompt?: string;
  input_Ratio: { base_width: number, base_height: number };
  output_Ratio: { base_width: number, base_height: number };
  type: OutPaintType;
}

export interface GetMusicByTaskIDReq {
  task_id: string;
}

export interface GetMusicByTaskIDRsp {
  status: Status;
  cos_url: string;
}

export interface AiPrompt {
  prompt: string;
  image?: RichMediaValue;
  cw?: number;
  sw?: number;
  referenceType?: AiImageReferenceType;
}

// 限流相关
export enum UserLevel {
  FREE = 1, // 免费用户
  SIGNED = 2, // 签约用户
  VIP = 3, // VIP用户

  // 前端定义
  BLACK = 99, // 黑名单用户
}
